import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import FormGenerator from '@sharedCR/common/formGenerator';
import { ADD_NEW_MORTGAGE } from 'app/pages/loanApplication/LoanApplication.constants';
import { mortgageFieldNames } from 'app/pages/loanApplication/LoanApplication.sharedConfigs';
import {
  checkIsAmountOfRentCollectedActive,
  checkIsSoldPropertyStatus,
  getExpenses,
  getFormLiabilities,
  getLiabilitiesPaymentsSum,
  getNetMonthlyRentalIncome,
  shouldResetCountyField,
} from 'app/pages/loanApplication/LoanApplication.utils';
import { FormNames } from 'app/services/autoLockForms/autoLockForms.constants';
import { onAutocompleteChangeInForm } from 'app/shared/componentsRestyled/coreDesignSystem/form';
import { ModalSize } from 'app/shared/componentsRestyled/coreDesignSystem/Modals';
import _ from 'lodash';

import {
  OccupancyType,
  PropertyMortgageLiabilityType,
  PropertyMortgageType,
} from 'constants/loanApplication';
import { rentCalculatedPurposeOptions } from 'constants/options';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import { getLiabilityOption } from 'utils/loanApplication';

import { openDialogAction } from 'store/global/actions';
import { getLocationsCountiesAction } from 'store/loanApplications/actions';
import {
  selectAlphabeticallySortedCountiesByStates,
  selectCommonInfoLoanApplication,
  selectFinancialInfoOwnersOptions,
  selectIsCountiesExist,
  selectMortgageLiabilityOptions,
} from 'store/loanApplications/selectors';

import { SectionContentPropTypes } from '../SectionContent.constants';

import LiabilityForm from './LiabilityForm';
import { getConfig, getConfigFields } from './Reo.config';
import { initialOtherExpensesState, PROPERTY_MODEL_TYPE } from './Reo.constants';
import {
  getFilteredLiabilityOptionsById,
  getIsOneOfMonthlyDependentParamsFilled,
  getUsedLiabilities,
  normalizeForm,
} from './Reo.utils';

const Reo = ({
  form,
  handleChange,
  isLoading,
  setInForm,
  setMultipleInForm,
  setForm,
  validateField,
  validationErrors,
  isSaving,
  recordId,
  setRecordConfig,
  initialFormValues,
  data,
  isDeleted,
  setIsLoading,
  setValidationErrors,
  isNewRecord,
  borrowerId,
  staticValidateField,
  pendingChanges,
  disabledPendingChangesHighlight,
  isOwnersClicked,
}) => {
  const recordData = data?.[recordId];

  const dispatch = useDispatch();

  const initialFormValuesRef = useRef({});

  const { loanPurpose, subjectPropertyId } = useSelector(selectCommonInfoLoanApplication);
  const ownersOptions = useSelector(selectFinancialInfoOwnersOptions);
  const isCountiesExist = useSelector(selectIsCountiesExist);
  const counties = useSelector(selectAlphabeticallySortedCountiesByStates);
  const liabilityOptions = useSelector(selectMortgageLiabilityOptions());

  const recordForm = form[recordId];
  const configFields = getConfigFields({ recordId });
  const taxesEscrowed = recordForm?.taxesEscrowed;
  const homeownersInsuranceEscrowed = recordForm?.homeownersInsuranceEscrowed;
  const hasFirstMortgage = recordForm?.hasFirstMortgage;
  const hasSecondMortgage = recordForm?.hasSecondMortgage;
  const associationFees = recordForm?.associationFees;
  const hasOtherExpenses = recordForm?.hasOtherExpenses;
  const isOtherExpensesEmpty = _.isEmpty(recordForm?.otherExpenses);
  const otherExpensesSource = isOtherExpensesEmpty ? recordData : recordForm;
  const otherExpensesCount = otherExpensesSource?.otherExpenses?.length;
  const state = recordForm?.addressstate?.value;
  const country = recordData?.address?.country;
  const principalAndInterest = recordData?.principalAndInterest;
  const monthlyPropertyTaxes = recordForm?.monthlyPropertyTaxes;
  const monthlyHomeownersInsurance = recordForm?.monthlyHomeownersInsurance;
  const monthlyPmi = recordForm?.monthlyPmi;
  const firstMortgageOption = recordForm?.[PropertyMortgageLiabilityType.FIRST];
  const secondMortgageOption = recordForm?.[PropertyMortgageLiabilityType.SECOND];
  const isFirstMortgageHeloc = firstMortgageOption?.heloc;
  const isSecondMortgageHeloc = secondMortgageOption?.heloc;
  const isSubjectProperty = recordData?.type === PROPERTY_MODEL_TYPE.SUBJECT_PROPERTY;
  const firstMortgageId = isSubjectProperty
    ? recordData?.[PropertyMortgageLiabilityType.FIRST]
    : firstMortgageOption?.value;
  const secondMortgageId = isSubjectProperty
    ? recordData?.[PropertyMortgageLiabilityType.SECOND]
    : secondMortgageOption?.value;
  const isAnyAssociationInfoFilled =
    !!recordForm?.associationFeesAmount ||
    !!recordForm?.associationFeesName ||
    !!recordForm?.associationFeesEmail ||
    !!recordForm?.associationFeesPhoneNumber;

  const isSoldPropertyStatus = checkIsSoldPropertyStatus(recordForm?.status?.value);
  const isInvestment = recordForm?.occupancy?.value === OccupancyType.INVESTMENT_PROPERTY;

  const usedLiabilities = useMemo(() => getUsedLiabilities({ data, form }), [data, form]);

  const filteredLiabilityOptionsById = useCallback(
    liabilityId =>
      getFilteredLiabilityOptionsById({
        subjectPropertyId,
        liabilityOptions,
        usedLiabilities,
        liabilityId,
      }),
    [liabilityOptions, usedLiabilities, subjectPropertyId],
  );

  const isAmountOfRentCollectedActive = useMemo(
    () =>
      checkIsAmountOfRentCollectedActive({
        occupancy: recordForm?.occupancy?.value,
        propertyType: recordForm?.propertyType?.value,
      }),
    [recordForm?.occupancy?.value, recordForm?.propertyType?.value],
  );

  const isOneOfMonthlyDependentParamsFilled = getIsOneOfMonthlyDependentParamsFilled({
    monthlyPropertyTaxes,
    monthlyHomeownersInsurance,
    monthlyPmi,
  });
  const isMonthlyPmiOrHomeownersInsuranceFilled = !!monthlyPmi || !!monthlyHomeownersInsurance;

  const onAddOtherExpenses = useCallback(
    () =>
      setForm(prev => ({
        ...prev,
        [recordId]: {
          ...prev[recordId],
          // eslint-disable-next-line no-unsafe-optional-chaining
          otherExpenses: [...prev[recordId]?.otherExpenses, ...initialOtherExpensesState],
        },
      })),
    [recordId, setForm],
  );

  const onOtherExpensesDelete = useCallback(
    deletedItemIndex => () =>
      setForm(prev => ({
        ...prev,
        [recordId]: {
          ...prev[recordId],
          otherExpenses: prev[recordId].otherExpenses.filter(
            (_, index) => index !== deletedItemIndex,
          ),
        },
      })),
    [recordId, setForm],
  );

  const onHasOtherExpensesChange = useCallback(
    e => {
      const { value } = e.target;
      const formattedValue = value === 'true';
      setInForm(configFields.hasOtherExpenses.nameInModel, formattedValue, false);

      setForm(prev => ({
        ...prev,
        [recordId]: {
          ...prev[recordId],
          otherExpenses: formattedValue ? initialOtherExpensesState : [''],
        },
      }));
    },
    [configFields.hasOtherExpenses.nameInModel, recordId, setForm, setInForm],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onAutocompleteChange = useCallback(
    onAutocompleteChangeInForm({ setInForm, validateField }),
    [setInForm, validateField],
  );

  const onLiabilityChange = useCallback(
    (e, option, { isFirstMortgage }) => {
      if (option?.value === ADD_NEW_MORTGAGE) {
        const onUpdateForm = liability =>
          setInForm(
            [recordId, '.', isFirstMortgage ? 'first' : 'second', 'MortgageliabilityId'].join(''),
            getLiabilityOption(liability),
          );

        return dispatch(
          openDialogAction({
            header: 'Add New Mortgage',
            isRestyled: true,
            size: ModalSize.XL,
            withDivider: true,
            body: dialogProps => <LiabilityForm onUpdateForm={onUpdateForm} {...dialogProps} />,
          }),
        );
      }

      onAutocompleteChange(e, option);
    },
    [dispatch, onAutocompleteChange, setInForm, recordId],
  );

  const expenses = getExpenses({
    ...recordForm,
    isSubjectProperty,
    taxesEscrowed: recordForm?.taxesEscrowed,
    homeownersInsuranceEscrowed: recordForm?.homeownersInsuranceEscrowed,
    ...(isInvestment && { supplementalInsurances: recordData?.supplementalInsurances }),
  });

  const netMonthlyRentalIncome = getNetMonthlyRentalIncome({
    ...recordForm,
    isSubjectProperty,
    principalAndInterest,
    status: recordForm?.status?.value,
    occupancy: recordForm?.occupancy?.value,
    propertyType: recordForm?.propertyType?.value,
    loanPurpose,
    expenses,
    liabilities: getFormLiabilities(recordForm),
  });

  const config = useMemo(
    () =>
      getConfig({
        recordId,
        associationFees,
        hasOtherExpenses,
        otherExpensesCount,
        onAddOtherExpenses,
        onOtherExpensesDelete,
        onHasOtherExpensesChange,
        onLiabilityChange,
        firstMortgageOptions: filteredLiabilityOptionsById(firstMortgageId),
        secondMortgageOptions: filteredLiabilityOptionsById(secondMortgageId),
        isAmountOfRentCollectedActive,
        isFirstMortgageHeloc,
        isSecondMortgageHeloc,
        hasFirstMortgage,
        hasSecondMortgage,
        taxesEscrowed,
        homeownersInsuranceEscrowed,
        isMonthlyPmiOrHomeownersInsuranceFilled,
        isSoldPropertyStatus,
        counties,
        state,
        ownersOptions,
        country,
        isNewRecord,
        isOwnersClicked,
        isSubjectProperty,
      }),
    [
      filteredLiabilityOptionsById,
      firstMortgageId,
      secondMortgageId,
      recordId,
      associationFees,
      hasOtherExpenses,
      otherExpensesCount,
      onAddOtherExpenses,
      onOtherExpensesDelete,
      onHasOtherExpensesChange,
      onLiabilityChange,
      isAmountOfRentCollectedActive,
      isFirstMortgageHeloc,
      isSecondMortgageHeloc,
      hasFirstMortgage,
      hasSecondMortgage,
      taxesEscrowed,
      homeownersInsuranceEscrowed,
      isMonthlyPmiOrHomeownersInsuranceFilled,
      isSoldPropertyStatus,
      counties,
      state,
      ownersOptions,
      country,
      isNewRecord,
      isOwnersClicked,
      isSubjectProperty,
    ],
  );

  useEffectSafeStrictMode(() => {
    if (isCountiesExist) {
      return;
    }

    setIsLoading(true);

    Promise.all([dispatch(getLocationsCountiesAction())])
      .catch(Error)
      .finally(() => setIsLoading(false));
  }, [dispatch, isCountiesExist, setIsLoading]);

  useEffect(() => {
    if (isDeleted) {
      return;
    }

    setRecordConfig(config, recordId);
  }, [config, setRecordConfig, isDeleted, recordId]);

  useEffect(() => {
    initialFormValuesRef.current = initialFormValues;
  }, [initialFormValues]);

  useEffect(() => {
    setForm(form =>
      normalizeForm({
        form,
        recordId,
        initialFormValuesRef,
        isNewRecord,
        borrowerId,
        ownersOptions,
      }),
    );
  }, [data, setForm, recordId, initialFormValues, isNewRecord, borrowerId, ownersOptions, config]);

  useEffect(() => {
    if (!isAmountOfRentCollectedActive) {
      return;
    }

    setMultipleInForm(
      {
        [configFields.expenses.nameInModel]: expenses,
        [configFields.netRent.nameInModel]: netMonthlyRentalIncome,
      },
      false,
    );
  }, [
    configFields.expenses.nameInModel,
    configFields.netRent.nameInModel,
    expenses,
    isAmountOfRentCollectedActive,
    netMonthlyRentalIncome,
    setMultipleInForm,
  ]);

  useEffect(() => {
    if (_.isNil(associationFees) || associationFees || !isAnyAssociationInfoFilled) {
      return;
    }

    [
      configFields.associationFeesAmount.nameInModel,
      configFields.associationFeesName.nameInModel,
      configFields.associationFeesEmail.nameInModel,
      configFields.associationFeesPhoneNumber.nameInModel,
    ].forEach(name => setInForm(name, null, false));
  }, [
    associationFees,
    configFields.associationFeesAmount.nameInModel,
    configFields.associationFeesEmail.nameInModel,
    configFields.associationFeesName.nameInModel,
    configFields.associationFeesPhoneNumber.nameInModel,
    isAnyAssociationInfoFilled,
    setInForm,
  ]);

  useEffect(() => {
    const prefix = `${recordId}.${PropertyMortgageType.FIRST}`;
    const firstMortgage = liabilityOptions.find(({ value }) => value === firstMortgageId);

    mortgageFieldNames.forEach(fieldName => {
      setInForm(`${prefix}${fieldName}`, firstMortgage?.[fieldName] ?? null, false);
    });

    if (isSoldPropertyStatus && firstMortgage) {
      setInForm(`${prefix}excludePayment`, true, false);
    }
  }, [firstMortgageId, setInForm, liabilityOptions, isSoldPropertyStatus, recordId]);

  useEffect(() => {
    const prefix = `${recordId}.${PropertyMortgageType.SECOND}`;
    const secondMortgage = liabilityOptions.find(({ value }) => value === secondMortgageId);

    mortgageFieldNames.forEach(fieldName => {
      setInForm(`${prefix}${fieldName}`, secondMortgage?.[fieldName] ?? null, false);
    });

    if (isSoldPropertyStatus && secondMortgage) {
      setInForm(`${prefix}excludePayment`, true, false);
    }
  }, [secondMortgageId, setInForm, liabilityOptions, isSoldPropertyStatus, recordId]);

  const firstMortgageMonthlyPayment = recordForm?.[`${PropertyMortgageType.FIRST}monthlyPayment`];
  const firstMortgageToBePaidOff =
    recordForm?.[`${PropertyMortgageType.FIRST}toBePaidOffAtOrBeforeClosing`];
  const firstMortgageExcludePayment = recordForm?.[`${PropertyMortgageType.FIRST}excludePayment`];

  const secondMortgageMonthlyPayment = recordForm?.[`${PropertyMortgageType.SECOND}monthlyPayment`];
  const secondMortgageToBePaidOff =
    recordForm?.[`${PropertyMortgageType.SECOND}toBePaidOffAtOrBeforeClosing`];
  const secondMortgageExcludePayment = recordForm?.[`${PropertyMortgageType.SECOND}excludePayment`];

  useEffect(() => {
    const sumOfMonthlyPayment = getLiabilitiesPaymentsSum([
      {
        monthlyPayment: firstMortgageMonthlyPayment,
        toBePaidOffAtOrBeforeClosing: firstMortgageToBePaidOff,
        excludePayment: firstMortgageExcludePayment,
      },
      {
        monthlyPayment: secondMortgageMonthlyPayment,
        toBePaidOffAtOrBeforeClosing: secondMortgageToBePaidOff,
        excludePayment: secondMortgageExcludePayment,
      },
    ]);

    const mortgagesPayment = isSubjectProperty ? principalAndInterest : sumOfMonthlyPayment;

    setInForm(configFields.mortgagesPayment.nameInModel, mortgagesPayment, false);
  }, [
    isSubjectProperty,
    principalAndInterest,
    firstMortgageMonthlyPayment,
    firstMortgageToBePaidOff,
    firstMortgageExcludePayment,
    secondMortgageMonthlyPayment,
    secondMortgageToBePaidOff,
    secondMortgageExcludePayment,
    configFields.mortgagesPayment.nameInModel,
    setInForm,
  ]);

  useEffect(() => {
    if (!isAmountOfRentCollectedActive || recordData?.rentCalculatedBasedOn) {
      return;
    }

    setInForm(
      configFields.rentCalculatedBasedOn.nameInModel,
      rentCalculatedPurposeOptions[0].value,
    );
  }, [
    isAmountOfRentCollectedActive,
    setInForm,
    recordData?.rentCalculatedBasedOn,
    configFields.rentCalculatedBasedOn.nameInModel,
  ]);

  useEffect(() => {
    if (!counties?.length) {
      return;
    }

    const shouldResetCounty = shouldResetCountyField({
      counties,
      formState: recordForm?.address?.state?.value,
      formCounty: recordForm?.address?.county?.value,
    });

    if (shouldResetCounty) {
      setInForm(`[${recordId}].address.county`, null, false);
    }
  }, [
    counties,
    recordForm?.address?.county?.value,
    recordForm?.address?.state?.value,
    recordId,
    setInForm,
  ]);

  const validateTaxesInsuranceEscrowedGroup = useCallback(() => {
    setValidationErrors(prevErrors => {
      const clone = _.cloneDeep(prevErrors);

      return {
        ...clone,
        [recordId]: {
          ...clone[recordId],
          monthlyHomeownersInsurance: '',
          monthlyPmi: '',
          monthlyPropertyTaxes: '',
        },
      };
    });
  }, [setValidationErrors, recordId]);

  useEffect(() => {
    if (
      !isOneOfMonthlyDependentParamsFilled ||
      (recordForm?.taxesEscrowed && recordForm?.homeownersInsuranceEscrowed)
    ) {
      return;
    }

    validateTaxesInsuranceEscrowedGroup();
  }, [
    validateTaxesInsuranceEscrowedGroup,
    isOneOfMonthlyDependentParamsFilled,
    recordForm?.taxesEscrowed,
    recordForm?.homeownersInsuranceEscrowed,
  ]);

  return (
    <FormGenerator
      isNestedForm
      config={config}
      disabledPendingChangesHighlight={disabledPendingChangesHighlight}
      form={form}
      formName={FormNames.REO}
      handleChange={handleChange}
      isDisabled={isSubjectProperty}
      isLoading={isSaving || isLoading}
      numberOfColumns={4}
      pendingChanges={pendingChanges}
      setInForm={setInForm}
      staticValidateField={staticValidateField}
      validateField={validateField}
      validationErrors={validationErrors}
    />
  );
};

Reo.propTypes = {
  ...SectionContentPropTypes,
};

export default Reo;
